import { Capacitor, registerPlugin } from '@capacitor/core';
import type { NdefRecord } from '@capgo/capacitor-nfc';

/**
 * The Android app around this same web app (Capacitor, see `app/` in the repo).
 *
 * The app loads the interface from the server like any browser does, so almost
 * nothing here knows it runs inside it. Where it has to — a download, the back
 * key, what the WebView lacks — the difference is decided HERE and nowhere else:
 * the rest of the tree asks {@link isNativeApp} and calls what this file offers.
 *
 * The plugins are imported only once the app is known to be native, so a
 * browser never loads them.
 */

/** True inside the Android app, false in every browser (and in tests). */
export function isNativeApp(): boolean {
    return Capacitor.isNativePlatform();
}

let shellVersion: string | null = null;

/**
 * The version of the installed app shell ("1.0.0"), next to the web app's own
 * `__APP_VERSION__`. Null in a browser, and until {@link startNativeApp} has
 * asked for it.
 */
export function nativeShellVersion(): string | null {
    return shellVersion;
}

/**
 * Once at start-up: what only the app needs. In a browser it does nothing.
 *
 * Android's back key: Capacitor's default closes the app outright, even with a
 * dialog open. Going back through the history instead behaves like the browser
 * back button the routes and the unsaved-changes guard are built around; with
 * nothing left to go back to, the app is sent to the background, not closed.
 */
export async function startNativeApp(): Promise<void> {
    if (!isNativeApp()) {
        return;
    }
    const { App } = await import('@capacitor/app');
    await App.addListener('appUrlOpen', ({ url }) => deliverOpenedUrl(url));
    const info = await App.getInfo();
    shellVersion = info.version;
    await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
            window.history.back();
        } else {
            void App.minimizeApp();
        }
    });
}

/** Links that opened the app before anybody listened (the start-up card). */
const openedUrls: string[] = [];
const openedUrlListeners = new Set<(url: string) => void>();

function deliverOpenedUrl(url: string) {
    if (openedUrlListeners.size === 0) {
        openedUrls.push(url);
        return;
    }
    openedUrlListeners.forEach((listener) => listener(url));
}

/**
 * A link that opened the app — in practice a login card, which the app takes
 * instead of the browser (see MainActivity): at start-up, or held to the
 * device while the app runs. Links from before the first listener are handed
 * to it at once. Never fires in a browser. Returns the unsubscribe.
 */
export function onOpenedWithUrl(listener: (url: string) => void): () => void {
    openedUrlListeners.add(listener);
    openedUrls.splice(0).forEach(listener);
    return () => {
        openedUrlListeners.delete(listener);
    };
}

/** Which device this is, as a photo records it. */
export interface DeviceIdentity {
    /** The name the device was given in Android's settings. */
    name: string;
    /** Android's id for this app on this device — survives renaming it. */
    id: string;
}

let deviceIdentity: Promise<DeviceIdentity> | null = null;

/**
 * Which device this is: name and id, empty in a browser (which learns
 * neither) and in a shell without the device plugin. Asked once, then kept —
 * renaming the device takes effect the next time the app starts.
 */
export function thisDevice(): Promise<DeviceIdentity> {
    if (!isNativeApp() || !Capacitor.isPluginAvailable('Device')) {
        return Promise.resolve({ name: '', id: '' });
    }
    deviceIdentity ??= import('@capacitor/device')
        .then(async ({ Device }) => {
            const [info, { identifier }] = await Promise.all([
                Device.getInfo(),
                Device.getId()
            ]);
            return { name: info.name ?? info.model, id: identifier };
        })
        .catch((error: unknown) => {
            console.error('Reading the device name failed', error);
            deviceIdentity = null;
            return { name: '', id: '' };
        });
    return deviceIdentity;
}

/**
 * The size a file is handed to the native side in. The bridge carries strings,
 * so each piece travels as base64; an ExcaBook ZIP can run to hundreds of
 * megabytes and must not become ONE such string.
 */
const SAVE_CHUNK_BYTES = 2 * 1024 * 1024;

/** Where the app's exports land: Documents/AST Data on the device. */
const EXPORT_FOLDER = 'AST Data';

/** Bytes per `String.fromCharCode` call — its arguments go on the stack. */
const CHAR_CODE_BATCH = 0x8000;

async function blobToBase64(blob: Blob): Promise<string> {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHAR_CODE_BATCH) {
        binary += String.fromCharCode(
            ...bytes.subarray(i, i + CHAR_CODE_BATCH)
        );
    }
    return btoa(binary);
}

const saveFailedListeners = new Set<(fileName: string) => void>();

/**
 * Be told when {@link saveFileInApp} could not write a file, so the screen can
 * say so — the download started by a click that has long returned. Returns the
 * unsubscribe.
 */
export function onSaveInAppFailed(
    listener: (fileName: string) => void
): () => void {
    saveFailedListeners.add(listener);
    return () => {
        saveFailedListeners.delete(listener);
    };
}

/**
 * The app's download: a WebView ignores `<a download>`, so the file is written
 * to Documents/AST Data piece by piece, then offered through Android's share
 * sheet — open it, send it, or leave it in the folder.
 *
 * Never rejects: a failure is logged and reported to {@link onSaveInAppFailed}.
 */
export async function saveFileInApp(
    blob: Blob,
    fileName: string
): Promise<void> {
    try {
        await writeAndShare(blob, fileName);
    } catch (error) {
        console.error('Saving the file in the app failed', error);
        saveFailedListeners.forEach((listener) => listener(fileName));
    }
}

async function writeAndShare(blob: Blob, fileName: string): Promise<void> {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const path = `${EXPORT_FOLDER}/${fileName}`;
    // At least one round, so an empty file is still written.
    let offset = 0;
    do {
        const data = await blobToBase64(
            blob.slice(offset, offset + SAVE_CHUNK_BYTES)
        );
        if (offset === 0) {
            await Filesystem.writeFile({
                path,
                data,
                directory: Directory.Documents,
                recursive: true
            });
        } else {
            await Filesystem.appendFile({
                path,
                data,
                directory: Directory.Documents
            });
        }
        offset += SAVE_CHUNK_BYTES;
    } while (offset < blob.size);
    const { uri } = await Filesystem.getUri({
        path,
        directory: Directory.Documents
    });
    const { Share } = await import('@capacitor/share');
    try {
        await Share.share({ title: fileName, files: [uri] });
    } catch {
        // Closing the share sheet rejects too; the file is saved either way.
    }
}

/**
 * True when the app can read and write NFC cards itself — the WebView has no
 * Web NFC. Checks the plugin, not just the app: the interface comes from the
 * server, so it can be newer than an installed shell that lacks it.
 */
export function hasNfcInApp(): boolean {
    return isNativeApp() && Capacitor.isPluginAvailable('CapacitorNfc');
}

/** NDEF Type Name Format "well-known", and its URI and text record types. */
const TNF_WELL_KNOWN = 1;
const RECORD_TYPE_URI = 0x55; // 'U'
const RECORD_TYPE_TEXT = 0x54; // 'T'
/** URI prefix codes of the NDEF URI record; 0 means "none abbreviated". */
const URI_PREFIXES = ['', 'http://www.', 'https://www.', 'http://', 'https://'];

/** An NDEF URI record for `url`, its scheme abbreviated as the format wants. */
function uriRecord(url: string) {
    // The longest prefix that fits: "https://www." over "https://".
    let code = 0;
    URI_PREFIXES.forEach((prefix, index) => {
        if (
            prefix.length > URI_PREFIXES[code].length &&
            url.startsWith(prefix)
        ) {
            code = index;
        }
    });
    const rest = new TextEncoder().encode(url.slice(URI_PREFIXES[code].length));
    return {
        tnf: TNF_WELL_KNOWN,
        type: [RECORD_TYPE_URI],
        id: [],
        payload: [code, ...rest]
    };
}

/** The text a URI or text record carries; null for any other record. */
function recordText(record: NdefRecord): string | null {
    if (record.tnf !== TNF_WELL_KNOWN || record.type.length !== 1) {
        return null;
    }
    const [status, ...bytes] = record.payload;
    if (record.type[0] === RECORD_TYPE_URI) {
        const prefix = URI_PREFIXES[status] ?? '';
        return prefix + new TextDecoder().decode(new Uint8Array(bytes));
    }
    if (record.type[0] === RECORD_TYPE_TEXT) {
        // Low six bits: length of the language code before the text; the top
        // bit: UTF-16 instead of UTF-8.
        const languageLength = status & 0x3f;
        const encoding = status & 0x80 ? 'utf-16' : 'utf-8';
        return new TextDecoder(encoding).decode(
            new Uint8Array(bytes.slice(languageLength))
        );
    }
    return null;
}

/**
 * Start the plugin's reader for as long as `signal` allows, telling `onTag`
 * about every card held against the device. Resolves once reading runs.
 * NFC switched off rejects — and opens Android's NFC settings on the way.
 */
async function readCards(
    onTag: (records: NdefRecord[]) => void,
    signal: AbortSignal
): Promise<void> {
    signal.throwIfAborted();
    const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
    const listener = await CapacitorNfc.addListener('nfcEvent', (event) =>
        onTag(event.tag.ndefMessage ?? [])
    );
    const stop = () => {
        void listener.remove();
        void CapacitorNfc.stopScanning().catch(() => {});
    };
    if (signal.aborted) {
        stop();
        signal.throwIfAborted();
    }
    signal.addEventListener('abort', stop, { once: true });
    try {
        await CapacitorNfc.startScanning();
    } catch (error) {
        signal.removeEventListener('abort', stop);
        stop();
        if ((error as { code?: string }).code === 'NFC_DISABLED') {
            void CapacitorNfc.showSettings().catch(() => {});
        }
        throw error;
    }
}

/**
 * The app's `NDEFReader.scan()`: `onCard` gets the texts of the URI and text
 * records of every card read, until `signal` aborts.
 */
export function scanNfcInApp(
    onCard: (texts: string[]) => void,
    signal: AbortSignal
): Promise<void> {
    return readCards((records) => {
        onCard(
            records
                .map(recordText)
                .filter((text): text is string => text !== null)
        );
    }, signal);
}

/**
 * The app's `NDEFReader.write()`: waits for a card, writes one URI record onto
 * it and resolves; rejects when the write fails or `signal` aborts.
 */
export async function writeNfcUrlInApp(
    url: string,
    signal: AbortSignal
): Promise<void> {
    signal.throwIfAborted();
    const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
    const scanning = new AbortController();
    const abort = () => scanning.abort(signal.reason);
    signal.addEventListener('abort', abort, { once: true });
    try {
        await new Promise<void>((resolve, reject) => {
            scanning.signal.addEventListener('abort', () =>
                reject(scanning.signal.reason)
            );
            let writing = false;
            readCards(() => {
                if (writing) {
                    return;
                }
                writing = true;
                CapacitorNfc.write({ records: [uriRecord(url)] }).then(
                    resolve,
                    reject
                );
            }, scanning.signal).catch(reject);
        });
    } finally {
        signal.removeEventListener('abort', abort);
        scanning.abort();
    }
}

interface PickedOriginal {
    /** A file:// copy in the app's cache, deleted once read. */
    uri: string;
    name: string;
    mimeType: string;
    /** False when Android only gave the copy without position. */
    original: boolean;
}

interface GalleryOriginalsPlugin {
    pick(): Promise<{
        files: PickedOriginal[];
    }>;
}

/** The app's own plugin, in `app/android` — no npm package. */
const GalleryOriginals =
    registerPlugin<GalleryOriginalsPlugin>('GalleryOriginals');

/**
 * True when the app can pick gallery photos WITH their position. The browser's
 * file input cannot: Android zeroes the GPS values of every photo it hands
 * over through Google's photo picker.
 */
export function hasGalleryOriginalsInApp(): boolean {
    return isNativeApp() && Capacitor.isPluginAvailable('GalleryOriginals');
}

function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Let the user pick photos in Google's photo picker, as the originals with
 * their position where Android allows it. Empty when the picker was closed.
 */
export async function pickGalleryPhotosInApp(): Promise<File[]> {
    const { files } = await GalleryOriginals.pick();
    const { Filesystem } = await import('@capacitor/filesystem');
    const photos: File[] = [];
    for (const picked of files) {
        try {
            const { data } = await Filesystem.readFile({ path: picked.uri });
            const content =
                typeof data === 'string' ? base64ToBytes(data) : data;
            photos.push(
                new File([content], picked.name, { type: picked.mimeType })
            );
            if (!picked.original) {
                console.warn(`${picked.name}: no original, no position`);
            }
        } finally {
            void Filesystem.deleteFile({ path: picked.uri }).catch(() => {});
        }
    }
    return photos;
}
